import { useEffect, useState, useRef, useCallback } from "react";
import { Box } from "@mui/material";
import { SearchBar } from "../searchBar/SearchBar";
import Text from "../text/Text";
import CommonHeader from "../CommonHeader";
import {
  apps,
  browse,
  noMiniAppFoundDescription,
  noMiniAppFoundTitle,
  search,
} from "../messages";
import CategoriesList from "./categories/CategoriesList";
import { callAPIInterface } from "../constants";
import { useSession } from "../../hooks/useSession";
import List from "./List";
import CommonSkeleton from "../CommonSkeleton";
import InfiniteScroll from "react-infinite-scroller";
import { noMiniAppFoundImg } from "../images";
import { useSelector } from "react-redux";
import InstalledAppDialog from "./InstalledAppDialog";
import AppDetailsDialog from "./AppDetailsDialog";

const SearchMiniApps = () => {
  const [searchValue, setSearchValue] = useState("");
  const [searchData, setSearchData] = useState([]);
  const [isSearchLoader, setIsSearchLoader] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextPage, setNextPage] = useState(null);

  const timeoutRef = useRef();
  const { session } = useSession();

  const { isOpenInstalledAppDialog, isAppDetailOpen } = useSelector(
    (state) => state.common
  );

  const commonStyle = {
    padding: "0 20px",
  };

  const handleSearch = useCallback(
    async (lines, nextPage) => {
      try {
        const response = await callAPIInterface("POST", `/search/mini-apps`, {
          query: `status:approved -restricted_countries:'${session?.account?.account?.country}' ${searchValue}`,
          limit: 15,
          ...(nextPage && { page: nextPage }),
        });
        if (!response?.has_more) {
          setHasMore(false);
        } else {
          setNextPage(response?.next_page);
        }
        setSearchData(lines.concat(response?.data));
      } catch (err) {
        console.log("Error while search API", err);
      } finally {
        setIsSearchLoader(false);
      }
    },
    [searchValue, session]
  );

  useEffect(() => {
    if (searchValue && session) {
      setIsSearchLoader(true);
      timeoutRef.current = setTimeout(() => {
        handleSearch([], nextPage);
      }, 1000);
    }

    return () => clearTimeout(timeoutRef.current);
  }, [searchValue, session]);

  const handleChange = useCallback((e) => {
    setSearchValue(e.target.value);
  }, []);

  const handleClear = useCallback(() => {
    setSearchValue("");
    setSearchData([]);
  }, []);

  const listSkeleton = useCallback(() => {
    return (
      <Box className="list-box" sx={{ padding: "12px 15px" }}>
        <Box display="flex" gap="10px">
          <CommonSkeleton
            variant="rectangular"
            height="56px"
            width="56px"
            borderRadius="14px"
          />
          <Box>
            <CommonSkeleton variant="text" />
            <CommonSkeleton variant="text" width="200px" />
          </Box>
        </Box>
      </Box>
    );
  }, []);

  const loadMore = useCallback(() => {
    hasMore && handleSearch(searchData, nextPage);
  }, [searchData]);

  const searchComponent = () => {
    return (
      <>
        {isSearchLoader ? (
          <Box className="search-data">{listSkeleton()}</Box>
        ) : !searchData?.length ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            height="calc(100% - 76px)"
            justifyContent="center"
            padding="0 20px"
            sx={{ backgroundColor: "white" }}
          >
            <img
              src={noMiniAppFoundImg}
              alt="not-found"
              height="44px"
              width="44px"
              style={{ marginBottom: "20px" }}
            />
            <Text
              className="default-text"
              size={20}
              font="semibold"
              align="center"
            >
              {noMiniAppFoundTitle}
            </Text>
            <Text className="grey-text" size={14} font="regular" align="center">
              {noMiniAppFoundDescription}
            </Text>
          </Box>
        ) : (
          <Box sx={commonStyle} className="search-data">
            <InfiniteScroll
              pageStart={0}
              loadMore={loadMore}
              initialLoad={false}
              hasMore={hasMore}
              useWindow={false}
              className="transaction-list"
              style={{ width: "100%" }}
            >
              {searchData?.map((data, idx) => {
                return (
                  <List
                    key={data.id || idx}
                    data={data}
                    idx={idx}
                    dataLength={searchData?.length}
                    isShowButton={false}
                  />
                );
              })}
            </InfiniteScroll>
          </Box>
        )}
      </>
    );
  };

  return (
    <>
      <CommonHeader textAfterIcon={apps} icon="backArrow" />
      <Box className="options-container">
        <Box className="search-wrapper">
          <SearchBar
            value={searchValue}
            handleChange={handleChange}
            handleClear={handleClear}
            placeHolder={search}
          />
        </Box>
        {searchValue ? (
          searchComponent()
        ) : (
          <>
            <Text
              className="default-text"
              size={20}
              font="bold"
              sx={commonStyle}
            >
              {browse}
            </Text>
            <Box className="category-section">
              <CategoriesList isShowHeading={false} />
            </Box>
          </>
        )}
      </Box>
      {isAppDetailOpen && <AppDetailsDialog />}
      {isOpenInstalledAppDialog && <InstalledAppDialog />}
    </>
  );
};

export default SearchMiniApps;
